/**
 * The VOIP render-decision sink the Session Flight Recorder observes `useVoipConnections` through
 * (AVS-4423 Task 1.8).
 *
 * `useVoipConnections` computes a render list by running several filters and dedupe passes in
 * sequence, dropping remotes for distinct reasons — a dead transport, an identity not yet
 * resolved, the dealer's own self-mirror, a duplicate, a fully-suppressed standby — and today
 * throws every one of those reasons away the instant it is computed. A recording can show that a
 * peer connection existed and that media was flowing, but not which remotes the UI actually
 * decided to render, and why the others were hidden — the exact distinction the 2026-09-04
 * multi-party incident review needed between a ghost peer (never there) and a stream-less tile
 * (there, but filtered).
 *
 * Two other surfaces report through this SAME sink, because the hook alone cannot see what either
 * of them narrows its own list down to:
 * - `MultiVoipInternal.tsx`, for the tile cap its own `sortedRemotes.slice(0, remoteSlots)`
 *   applies — without this, a dual-view call would report every remote as rendered when only one
 *   tile is actually on screen (fix round 1).
 * - `SingularVoip.tsx`, for the ONE tile it actually shows (`remoteConnections[activeConnectionId]`)
 *   — the default web-component embed (`Voip/index.tsx` mounts it whenever neither `multi` nor
 *   `dual` is set, so `MultiVoipInternal` is not mounted there at all) would otherwise have NO
 *   tile-cap event whatsoever, and the hook's own event alone would say all N rendered (fix round
 *   2). Not a slice (its pick need not be `sortedRemotes[0]` — `resolveActiveConnectionId` has its
 *   own sticky-previous/lowest-id tie-breaks the sort order does not reproduce), so it carries its
 *   own reason, `'no-tile'` (final review R2, renamed from `'single-tile'`), rather than reusing
 *   `'tile-cap'`.
 *
 * `source` names which of the three emitted a given list — required so a reader can tell an
 * uncapped `MultiVoipInternal`/`SingularVoip` event (`hidden: []`, because nothing needed capping
 * that render) apart from the hook's own event (which is where identity-based `hidden` entries,
 * e.g. `dead-transport`, actually live), and required by `rtcTap.ts` to key its de-duplication PER
 * SOURCE — the three emitters interleave, and a single shared fingerprint went inert the moment a
 * second, differently-shaped source started writing between repeats of the first (fix round 2).
 *
 * **Zero imports.** Same discipline as `utils/ui/mediaPlaybackSink.ts` and `utils/ui/app.ts`
 * (`setListenerGuardReporter` / `setEmitRecorder`, `utils/events.ts:61`/`:105`): a module-level
 * settable function defaulting to a no-op, called from INSIDE the owning code, wrapped in its own
 * try/catch so a throwing sink can never break the render computation it is observing. The payload
 * shape is declared locally rather than imported from `utils/ui/voipRemotes.ts` so this module
 * never depends on anything beyond its own file.
 *
 * **The caller reports; it does not decide.** This module only carries the fact — which remotes
 * rendered, and why the rest did not. Deciding the recorder LANE, the de-duplication (a memo/effect
 * can re-run with an identical result) and the priority for that fact is
 * `telemetry/recorder/taps/rtcTap.ts`'s job, the same separation `mediaPlaybackSink.ts` keeps from
 * the tap that registers it.
 */

export type TVoipHiddenReason =
  | 'dead-transport'
  | 'identity-pending'
  | 'self-mirror'
  /**
   * A MASTER's own OTHER tab, distinct from `'self-mirror'` (a standby's own loopback) — final
   * review R1. The two point in OPPOSITE directions in a takeover investigation, and before this
   * fix round both pushed `'self-mirror'`. See `utils/ui/voipRemotes.ts`'s own copy of this union
   * for the stage that produces it.
   */
  | 'own-tab'
  | 'dedupe'
  | 'suppressed'
  | 'tile-cap'
  /**
   * Final review R2, renamed from `'single-tile'`. Still means "no video tile", never "no audio" —
   * see the note on {@link TVoipRenderList.hidden} below.
   */
  | 'no-tile'
  | 'unexplained';

/** Which surface computed a given render list — see the module doc for why this is required. */
export type TVoipRenderSource = 'hook' | 'multi-voip' | 'singular-voip';

export type TVoipRenderList = {
  source: TVoipRenderSource;
  rendered: string[];
  /**
   * "Hidden" means no VIDEO TILE, never "no audio" (final review R2 — the payload itself carried
   * no such qualifier, and the module doc explaining it lives in THIS file, which a shard reader
   * never opens). `RemoteAudioSinks` renders at the root, decoupled from the tile list since
   * AVS-4397, and routes every remote's audio regardless of `'tile-cap'`/`'no-tile'`/any other
   * reason here — a remote in this array can be fully audible.
   */
  hidden: Array<{ id: string; reason: TVoipHiddenReason }>;
};

export type TVoipRenderSink = (list: TVoipRenderList) => void;

function noopVoipRenderSink(): void {
  // Default sink: no recorder installed, and `reportVoipRenderList` pays one no-op call.
}

let voipRenderSink: TVoipRenderSink = noopVoipRenderSink;

/** Register the sink. Pass `null` to restore the no-op. */
export function setVoipRenderSink(sink: TVoipRenderSink | null): void {
  voipRenderSink = sink ?? noopVoipRenderSink;
}

/**
 * True once a real sink is registered (final review C3) — i.e. the recorder installed
 * {@link setVoipRenderSink}, which only happens on dev/prelive. `describeRenderList` runs several
 * filter/dedupe passes over the remote list; the three reporting effects that call it (this
 * module's own callers) can check this FIRST and skip that work entirely when nothing will ever
 * read the result, rather than computing it and handing it to a no-op every VOIP commit for the
 * life of a production tab.
 */
export function isVoipRenderSinkActive(): boolean {
  return voipRenderSink !== noopVoipRenderSink;
}

/**
 * Report one render-list computation. Called from `useVoipConnections`'s own reporting effect,
 * `MultiVoipInternal.tsx`'s tile-cap reporting effect, and `SingularVoip.tsx`'s single-tile
 * reporting effect; never throws into its caller, because a throwing sink must not be the reason a
 * VOIP render decision fails to compute or commit.
 */
export function reportVoipRenderList(list: TVoipRenderList): void {
  try {
    voipRenderSink(list);
  } catch (error) {
    console.error('[voipRenderSink] sink threw and was isolated', error);
  }
}
